/**
 * Config to API Params Converter
 *
 * Utilitário para converter configurações do localStorage em parâmetros da API
 * para requests do endpoint /orders/today e WebSocket
 */

import type { FilaConfig } from '../types/config';
import type { TodayOrdersParams } from '@services/api/orderTypes';

/**
 * Converte as configurações da fila para parâmetros da API /orders/today
 * Com recuperação automática de establishmentId se necessário
 *
 * Mapeamento:
 * - config.orderType ('fila' | 'lancheira') → status ('fila' | 'preparando' | 'pronto' para lancheira)
 * - config.establishmentId → establishment_id
 * - config.selectedInterval → interval_id (quando autoIntervalChange=false)
 * - config.autoIntervalChange=true → interval_id será dinâmico (gerenciado pelo interval manager)
 * - currentIntervalId → interval_id (prioridade sobre config.selectedInterval quando fornecido)
 */
export function configToApiParams(config: FilaConfig, currentIntervalId?: string): TodayOrdersParams {
  // Tentar recuperar establishmentId se não existir
  let establishmentId = config.establishmentId;

  if (!establishmentId) {
    // a função devolve null quando não acha; o params usa undefined
    establishmentId = getEstablishmentIdFromSources() ?? undefined;
    if (establishmentId) {
      console.log('🔄 configToApiParams: establishmentId recuperado automaticamente:', establishmentId);
      // Atualizar config silenciosamente
      const updatedConfig = { ...config, establishmentId };
      try {
        localStorage.setItem('fila_digital_config', JSON.stringify(updatedConfig));
      } catch (error) {
        console.error('Erro ao salvar configuração recuperada:', error);
      }
    }
  }

  const params: TodayOrdersParams = {
    establishment_id: establishmentId || '',
  };

  // Se não tiver establishment_id após recuperação, retornar params vazios
  if (!establishmentId) {
    console.warn('⚠️ configToApiParams: establishment_id não encontrado em nenhuma fonte. Sistema funcionará sem dados.');
    return {
      establishment_id: '',
      sort_by: 'created_at',
      sort_order: 'asc',
      limit: 0, // Não buscar dados sem establishment
    };
  }

  // NÃO passar status para buscar TODAS as colunas (fila, preparando, pronto, etc)
  // A filtragem será feita no frontend

  // Definir interval_id baseado na configuração e parâmetro recebido
  if (config.autoIntervalChange) {
    // Modo automático: usar interval_id do currentInterval (salvo do /current)
    if (config.currentInterval && config.currentInterval.id) {
      params.interval_id = config.currentInterval.id;
      console.log('🔧 Auto interval: usando interval_id do currentInterval:', config.currentInterval.id);
    } else if (currentIntervalId) {
      // Fallback: usar currentIntervalId do parâmetro
      params.interval_id = currentIntervalId;
      console.log('🔧 Auto interval: usando interval_id do parâmetro:', currentIntervalId);
    } else {
      console.log('🔧 Auto interval: nenhum interval_id disponível, sem parâmetro intervalo');
    }
  } else {
    // Modo manual: prioridade máxima para intervalo selecionado manualmente
    if (config.selectedInterval) {
      // PRIORIDADE 1: Usar intervalo selecionado manualmente
      params.interval_id = config.selectedInterval;
      console.log('🔧 Manual interval: usando selectedInterval (seleção manual):', config.selectedInterval);
    } else if (currentIntervalId) {
      // PRIORIDADE 2: Usar interval_id passado como parâmetro
      params.interval_id = currentIntervalId;
      console.log('🔧 Manual interval: usando currentIntervalId (parâmetro):', currentIntervalId);
    } else if (config.currentInterval && config.currentInterval.id) {
      // PRIORIDADE 3: Usar currentInterval (fallback)
      params.interval_id = config.currentInterval.id;
      console.log('🔧 Manual interval: usando currentInterval (fallback):', config.currentInterval.id);
    } else {
      console.log('🔧 Manual interval: nenhum interval_id disponível');
    }
  }

  // Parâmetros adicionais para otimização
  params.sort_by = 'created_at';
  params.sort_order = 'asc';
  params.limit = 100; // Limitar para não sobrecarregar
  params.orderType = config.orderType;

  console.log('🔧 Config to API Params - interval_id:', params.interval_id || 'undefined');

  return params;
}

/**
 * Obtém establishmentId de diferentes fontes em ordem de prioridade
 */
function getEstablishmentIdFromSources(): string | null {
  try {
    // 1. Tentar do localStorage (sessão atual)
    const currentEstablishment = localStorage.getItem('current_establishment');
    if (currentEstablishment) {
      const parsed = JSON.parse(currentEstablishment);
      if (parsed?.id) {
        return parsed.id;
      }
    }

    // 2. Tentar do user profile
    const userInfo = localStorage.getItem('user_info');
    if (userInfo) {
      const parsed = JSON.parse(userInfo);
      const activeProfile = parsed?.profiles?.find((profile: any) => profile.active);
      if (activeProfile?.establishment_id) {
        return activeProfile.establishment_id;
      }
    }

    return null;
  } catch (error) {
    console.error('Erro ao buscar establishmentId:', error);
    return null;
  }
}

/**
 * Gera a chave para o WebSocket baseada nas configurações
 * Isso ajuda a identificar conexões WebSocket diferentes baseadas nas configurações
 */
export function generateWebSocketKey(config: FilaConfig, currentIntervalId?: string): string {
  const parts = [
    config.establishmentId || 'no-establishment',
    config.orderType,
    config.autoIntervalChange ? 'auto' : 'manual',
    config.autoIntervalChange && currentIntervalId ? currentIntervalId : config.selectedInterval || 'no-interval',
  ];

  return parts.join('-');
}

/**
 * Verifica se as configurações são válidas para fazer requests (modo tolerante)
 */
export function validateConfigForRequest(config: FilaConfig): { isValid: boolean; errors: string[] } {
  const errors: string[] = [];

  // Tentar recuperar establishmentId se não existir
  let establishmentId = config.establishmentId;
  if (!establishmentId) {
    // a função devolve null quando não acha; o params usa undefined
    establishmentId = getEstablishmentIdFromSources() ?? undefined;
    if (establishmentId) {
      console.log('🔄 validateConfigForRequest: establishmentId recuperado automaticamente');
    } else {
      console.warn('⚠️ validateConfigForRequest: establishmentId não encontrado, sistema funcionará limitado');
      errors.push('Estabelecimento não configurado - funcionamento limitado');
    }
  }

  // Sistema sempre considerado válido agora, apenas com warnings
  // Permitir funcionamento mesmo sem configuração completa
  return {
    isValid: true, // Sempre válido para permitir funcionamento básico
    errors,
  };
}

/**
 * Mapeia o tipo de pedido da configuração para o canal da API
 */
export function configOrderTypeToApiChannel(orderType: 'queue' | 'lunchbox'): string {
  switch (orderType) {
    case 'queue':
      return 'queue'; // Pedidos da fila digital
    case 'lunchbox':
      return 'lunchbox'; // Pedidos da lancheira digital
    default:
      return 'queue';
  }
}
